/**
 * Permission checking utilities
 * Use these functions to check if a user has specific permissions
 */

/**
 * Check if user has a specific permission
 * @param {Object} user - User object from useAuth hook
 * @param {string} permissionCode - Permission code to check (e.g., 'purchase_orders')
 * @returns {boolean}
 */
export const hasPermission = (user, permissionCode) => {
  if (!user) return false
  
  // Admins always have access
  if (user.role_id === 1) return true
  
  // Check if user has the permission
  const permissions = user.permission_codes || user.permissions?.map(p => p.code) || []
  return permissions.includes(permissionCode)
}

/**
 * Check if user has any of the specified permissions
 * @param {Object} user - User object from useAuth hook
 * @param {string[]} permissionCodes - Array of permission codes to check
 * @returns {boolean}
 */
export const hasAnyPermission = (user, permissionCodes) => {
  if (!user) return false
  
  // Admins always have access
  if (user.role_id === 1) return true
  
  // Check if user has any of the permissions
  const userPermissions = user.permission_codes || user.permissions?.map(p => p.code) || []
  return permissionCodes.some(code => userPermissions.includes(code))
}

/**
 * Check if user has all of the specified permissions
 * @param {Object} user - User object from useAuth hook
 * @param {string[]} permissionCodes - Array of permission codes to check
 * @returns {boolean}
 */
export const hasAllPermissions = (user, permissionCodes) => {
  if (!user) return false
  
  // Admins always have access
  if (user.role_id === 1) return true
  
  // Check if user has all of the permissions
  const userPermissions = user.permission_codes || user.permissions?.map(p => p.code) || []
  return permissionCodes.every(code => userPermissions.includes(code))
}

/**
 * Check if user can view all stock (not just assigned location)
 * @param {Object} user - User object from useAuth hook
 * @returns {boolean}
 */
export const canViewAllStock = (user) => {
  return hasPermission(user, 'view_all_stock')
}

/**
 * Check if user can access purchase orders
 * @param {Object} user - User object from useAuth hook
 * @returns {boolean}
 */
export const canAccessPurchaseOrders = (user) => {
  return hasPermission(user, 'purchase_orders')
}

export const canAccessDefectSheets = (user) => {
  return hasPermission(user, 'defect_sheets')
}

export const canAccessFieldJobs = (user) => {
  return hasPermission(user, 'field_jobs')
}

export const canAccessFieldHours = (user) => {
  return hasPermission(user, 'field_hours')
}

export const canViewQuotations = (user) => {
  return hasPermission(user, 'quotations_view')
}

export const canViewDefectSheets = (user) => {
  return hasPermission(user, 'defect_sheets_view') || hasPermission(user, 'defect_sheets')
}



